import { PageController } from '@client/components/_core/layout/PageController';
import type { Mention, MentionItem } from '@client/components/_ui/data-entry/TipTap';
import { TipTapInput } from '@client/components/_ui/data-entry/TipTap';
import { useThemeStore } from '@client/stores/themeStore';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Divider, Form, Switch, Typography } from 'antd';
import { Moon, Sun } from 'lucide-react';
import { useState } from 'react';

const { Title, Text, Paragraph } = Typography;

export const Route = createFileRoute('/console/$orgId/component-showcase/tiptap-test')({
    staticData: {
        breadcrumb: { title: 'TipTap Editor Test' },
        featureFlag: 'enable-debug-info',
    },
    component: TipTapTestPage,
});

// Sample mentions data for demonstration
const SAMPLE_MENTIONS: Mention[] = [
    {
        label: 'Fields',
        options: [
            { id: 'field-1', value: 'First Name', type: 'field', metadata: 'Text' },
            { id: 'field-2', value: 'Last Name', type: 'field', metadata: 'Text' },
            { id: 'field-3', value: 'Email Address', type: 'field', metadata: 'Email' },
            { id: 'field-4', value: 'Phone Number', type: 'field', metadata: 'Phone' },
        ],
    },
    {
        label: 'Variables',
        options: [
            { id: 'var-1', value: 'Current Date', type: 'variable', metadata: 'System' },
            { id: 'var-2', value: 'Organisation Name', type: 'variable', metadata: 'System' },
            { id: 'var-3', value: 'User Email', type: 'variable', metadata: 'System' },
        ],
    },
    {
        label: 'Users',
        options: [
            { id: 'user-1', value: 'John Doe', type: 'user' },
            { id: 'user-2', value: 'Jane Smith', type: 'user' },
            { id: 'user-3', value: 'Bob Johnson', type: 'user' },
        ],
    },
];

function TipTapTestPage() {
    const { theme, setTheme } = useThemeStore();
    const isDark =
        theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    // State for different editor demos
    const [basicValue, setBasicValue] = useState<string>('');
    const [aiValue, setAiValue] = useState<string>('');
    const [mentionsValue, setMentionsValue] = useState<string>('');
    const [imageValue, setImageValue] = useState<string>('');
    const [readOnlyValue] = useState<string>(
        '<h2>Welcome to TipTap Editor</h2><p>This is a <strong>read-only</strong> example showing formatted content.</p><ul><li>Rich text formatting</li><li>Lists and headings</li><li>Links and more</li></ul>',
    );

    return (
        <PageController route={Route}>
            <div className="mx-auto max-w-4xl p-6">
                {/* Header */}
                <div className="mb-8 flex items-start justify-between">
                    <div>
                        <Title level={2}>TipTap Editor Test Page</Title>
                        <Paragraph className="max-w-2xl text-muted-foreground">
                            Test the new TipTap-based rich text editor component. This modern editor will replace the
                            legacy Quill-based RichTextInput component throughout the application.
                        </Paragraph>
                    </div>
                    <div className="flex items-center gap-3 rounded-lg border border-separator bg-card px-4 py-2">
                        <Sun size={16} className={!isDark ? 'text-accent-500' : 'text-muted-foreground'} />
                        <Switch
                            checked={isDark}
                            onChange={(checked) => setTheme(checked ? 'dark' : 'light')}
                            checkedChildren={<Moon size={12} />}
                            unCheckedChildren={<Sun size={12} />}
                        />
                        <Moon size={16} className={isDark ? 'text-accent-500' : 'text-muted-foreground'} />
                    </div>
                </div>

                {/* Basic Editor */}
                <Card
                    title={
                        <div>
                            <Title level={4} className="!mb-0">
                                Basic Editor
                            </Title>
                            <Text type="secondary" className="text-sm">
                                Standard rich text editor with formatting toolbar
                            </Text>
                        </div>
                    }
                    className="mb-6"
                >
                    <div className="space-y-4">
                        <TipTapInput
                            value={basicValue}
                            onChange={setBasicValue}
                            placeholder="Start typing your content here..."
                        />
                        <Divider />
                        <div>
                            <Text type="secondary" className="mb-2 block font-medium text-xs">
                                HTML Output:
                            </Text>
                            <pre className="max-h-32 overflow-auto rounded bg-muted p-3 text-xs">
                                {basicValue || '<empty>'}
                            </pre>
                        </div>
                    </div>
                </Card>

                {/* Editor with AI assistant */}
                <Card
                    title={
                        <div>
                            <Title level={4} className="!mb-0">
                                Editor with AI assistant
                            </Title>
                            <Text type="secondary" className="text-sm">
                                AI assistant button in the toolbar — select text to act on the selection, or leave the
                                cursor anywhere to act on the whole document
                            </Text>
                        </div>
                    }
                    className="mb-6"
                >
                    <div className="space-y-4">
                        <TipTapInput
                            value={aiValue}
                            onChange={setAiValue}
                            aiAssistant={{ relatedFieldLabel: 'Showcase rich text' }}
                            placeholder="Type something, then try the sparkles button in the toolbar..."
                        />
                        <div className="rounded border border-separator bg-muted/50 p-3 text-muted-foreground text-xs">
                            <strong>Tip:</strong> With a text selection, transform actions (grammar, translate, improve,
                            enhance) offer "Replace selection"; without one they offer "Replace all" or "Append". All
                            insertions are undoable with Ctrl+Z.
                        </div>
                        <Divider />
                        <div>
                            <Text type="secondary" className="mb-2 block font-medium text-xs">
                                HTML Output:
                            </Text>
                            <pre className="max-h-32 overflow-auto rounded bg-muted p-3 text-xs">
                                {aiValue || '<empty>'}
                            </pre>
                        </div>
                    </div>
                </Card>

                {/* Editor with Mentions */}
                <Card
                    title={
                        <div>
                            <Title level={4} className="!mb-0">
                                Editor with Mentions
                            </Title>
                            <Text type="secondary" className="text-sm">
                                Type @ to trigger the mentions popup with grouped suggestions
                            </Text>
                        </div>
                    }
                    className="mb-6"
                >
                    <div className="space-y-4">
                        <TipTapInput
                            value={mentionsValue}
                            onChange={setMentionsValue}
                            mentions={SAMPLE_MENTIONS}
                            placeholder="Type @ to mention fields, variables, or users..."
                        />
                        <Divider />
                        <div>
                            <Text type="secondary" className="mb-2 block font-medium text-xs">
                                Available Mentions:
                            </Text>
                            <div className="flex flex-wrap gap-2">
                                {SAMPLE_MENTIONS.map((group) =>
                                    'label' in group && 'options' in group ? (
                                        <div key={group.label} className="rounded bg-muted px-2 py-1 text-xs">
                                            <span className="font-medium">{group.label}:</span>{' '}
                                            {group.options.map((o: MentionItem) => o.value).join(', ')}
                                        </div>
                                    ) : null,
                                )}
                            </div>
                        </div>
                        <div>
                            <Text type="secondary" className="mb-2 block font-medium text-xs">
                                HTML Output:
                            </Text>
                            <pre className="max-h-32 overflow-auto rounded bg-muted p-3 text-xs">
                                {mentionsValue || '<empty>'}
                            </pre>
                        </div>
                    </div>
                </Card>

                {/* Editor with Image Upload */}
                <Card
                    title={
                        <div>
                            <Title level={4} className="!mb-0">
                                Editor with Image Upload
                            </Title>
                            <Text type="secondary" className="text-sm">
                                Supports image upload via toolbar button, drag-and-drop, or paste
                            </Text>
                        </div>
                    }
                    className="mb-6"
                >
                    <div className="space-y-4">
                        <TipTapInput
                            value={imageValue}
                            onChange={setImageValue}
                            enableImageUpload
                            placeholder="Add text and images here..."
                        />
                        <div className="rounded border border-separator bg-muted/50 p-3 text-muted-foreground text-xs">
                            <strong>Tip:</strong> You can upload images by:
                            <ul className="mt-1 list-inside list-disc">
                                <li>Clicking the image button in the toolbar</li>
                                <li>Dragging and dropping an image onto the editor</li>
                                <li>Pasting an image from clipboard</li>
                            </ul>
                        </div>
                        <Divider />
                        <div>
                            <Text type="secondary" className="mb-2 block font-medium text-xs">
                                HTML Output:
                            </Text>
                            <pre className="max-h-32 overflow-auto rounded bg-muted p-3 text-xs">
                                {imageValue || '<empty>'}
                            </pre>
                        </div>
                    </div>
                </Card>

                {/* Read-Only Mode */}
                <Card
                    title={
                        <div>
                            <Title level={4} className="!mb-0">
                                Read-Only Mode
                            </Title>
                            <Text type="secondary" className="text-sm">
                                Display rich content without editing capabilities
                            </Text>
                        </div>
                    }
                    className="mb-6"
                >
                    <TipTapInput value={readOnlyValue} readOnly />
                </Card>

                {/* Form Integration */}
                <Card
                    title={
                        <div>
                            <Title level={4} className="!mb-0">
                                Form Integration
                            </Title>
                            <Text type="secondary" className="text-sm">
                                Works with Ant Design Form component
                            </Text>
                        </div>
                    }
                    className="mb-6"
                >
                    <Form layout="vertical">
                        <Form.Item
                            label="Description"
                            name="description"
                            rules={[{ required: true, message: 'Please enter a description' }]}
                        >
                            <TipTapInput placeholder="Enter description..." />
                        </Form.Item>
                        <Form.Item label="Notes" name="notes">
                            <TipTapInput mentions={SAMPLE_MENTIONS} placeholder="Add notes (@ for mentions)..." />
                        </Form.Item>
                    </Form>
                </Card>

                {/* Implementation Notes */}
                <Card
                    title={
                        <div>
                            <Title level={4} className="!mb-0">
                                Implementation Notes
                            </Title>
                        </div>
                    }
                >
                    <Paragraph className="mb-4 text-muted-foreground italic">
                        Import from{' '}
                        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                            @client/components/_ui/data-entry/TipTap
                        </code>
                    </Paragraph>

                    <div className="space-y-4">
                        <div>
                            <Text strong className="mb-2 block">
                                Features
                            </Text>
                            <ul className="list-disc space-y-1 pl-5 text-muted-foreground text-sm">
                                <li>
                                    <strong>Rich text formatting</strong>: Headings, bold, italic, underline,
                                    strikethrough
                                </li>
                                <li>
                                    <strong>Lists</strong>: Ordered and bullet lists with indentation
                                </li>
                                <li>
                                    <strong>Links</strong>: Inline link editing with click-to-edit popover
                                </li>
                                <li>
                                    <strong>Mentions</strong>: @-triggered autocomplete with grouped suggestions
                                </li>
                                <li>
                                    <strong>Images</strong>: Upload, drag-and-drop, paste (when enabled)
                                </li>
                            </ul>
                        </div>

                        <Divider />

                        <div>
                            <Text strong className="mb-2 block">
                                Props
                            </Text>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-separator border-b">
                                            <th className="py-2 pr-4 text-left font-medium">Prop</th>
                                            <th className="px-4 py-2 text-left font-medium">Type</th>
                                            <th className="px-4 py-2 text-left font-medium">Description</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-muted-foreground">
                                        <tr className="border-separator border-b">
                                            <td className="py-2 pr-4 font-mono text-xs">value</td>
                                            <td className="px-4 py-2 font-mono text-xs">string</td>
                                            <td className="px-4 py-2">HTML content value</td>
                                        </tr>
                                        <tr className="border-separator border-b">
                                            <td className="py-2 pr-4 font-mono text-xs">onChange</td>
                                            <td className="px-4 py-2 font-mono text-xs">(value: string) =&gt; void</td>
                                            <td className="px-4 py-2">Called when content changes</td>
                                        </tr>
                                        <tr className="border-separator border-b">
                                            <td className="py-2 pr-4 font-mono text-xs">placeholder</td>
                                            <td className="px-4 py-2 font-mono text-xs">string</td>
                                            <td className="px-4 py-2">Placeholder text when empty</td>
                                        </tr>
                                        <tr className="border-separator border-b">
                                            <td className="py-2 pr-4 font-mono text-xs">readOnly</td>
                                            <td className="px-4 py-2 font-mono text-xs">boolean</td>
                                            <td className="px-4 py-2">Disable editing</td>
                                        </tr>
                                        <tr className="border-separator border-b">
                                            <td className="py-2 pr-4 font-mono text-xs">autoFocus</td>
                                            <td className="px-4 py-2 font-mono text-xs">boolean</td>
                                            <td className="px-4 py-2">Focus editor on mount</td>
                                        </tr>
                                        <tr className="border-separator border-b">
                                            <td className="py-2 pr-4 font-mono text-xs">mentions</td>
                                            <td className="px-4 py-2 font-mono text-xs">Mention[]</td>
                                            <td className="px-4 py-2">Mention suggestions (grouped or flat)</td>
                                        </tr>
                                        <tr className="border-separator border-b">
                                            <td className="py-2 pr-4 font-mono text-xs">enableImageUpload</td>
                                            <td className="px-4 py-2 font-mono text-xs">boolean</td>
                                            <td className="px-4 py-2">Enable image upload functionality</td>
                                        </tr>
                                        <tr>
                                            <td className="py-2 pr-4 font-mono text-xs">className</td>
                                            <td className="px-4 py-2 font-mono text-xs">string</td>
                                            <td className="px-4 py-2">Additional CSS classes</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <Divider />

                        <div>
                            <Text strong className="mb-2 block">
                                Migration status
                            </Text>
                            <Text type="secondary" className="text-sm">
                                Quill has been fully removed. TipTapInput is the sole rich text editor. Legacy Quill
                                HTML stored in the database is automatically transformed via quillToTipTapTransform at
                                both editor load and display time.
                            </Text>
                        </div>
                    </div>
                </Card>
            </div>
        </PageController>
    );
}
